import React, { useState, useEffect } from 'react';
import { listDeclarations, recordPayment } from '../services/assujettiService';

export default function V4PaymentsPage() {
  const [dossier, setDossier] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ amount: '', currency: 'USD', reference: '', method: 'bank_transfer' });
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await listDeclarations();
        const list = Array.isArray(data) ? data : [];
        setDossier(list[0] || null);
      } catch (err: any) {
        setError(err.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dossier?.id || !form.amount || !form.reference) return;
    setSubmitting(true);
    setError(null);
    try {
      await recordPayment({
        declaration_id: dossier.id,
        amount: parseFloat(form.amount),
        currency: form.currency,
        reference: form.reference,
        method: form.method,
      });
      setSuccess('Paiement enregistre avec succes');
      setShowForm(false);
      setForm({ amount: '', currency: 'USD', reference: '', method: 'bank_transfer' });
    } catch (err: any) {
      setError(err.message || 'Erreur lors du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Chargement...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Paiements multi-devises</h3>

        {dossier ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div className="bg-gray-50 border rounded-xl p-4">
                <div className="text-xs text-gray-500 mb-1">Part USD due</div>
                <div className="font-bold">{dossier.tax_usd != null ? `${Number(dossier.tax_usd).toLocaleString()} USD` : '--'}</div>
              </div>
              <div className="bg-gray-50 border rounded-xl p-4">
                <div className="text-xs text-gray-500 mb-1">Part CDF due</div>
                <div className="font-bold">{dossier.tax_cdf != null ? `${Number(dossier.tax_cdf).toLocaleString()} CDF` : '--'}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              <button onClick={() => { setForm(f => ({ ...f, currency: 'USD' })); setShowForm(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700">Payer USD + CDF</button>
              <button onClick={() => { setForm(f => ({ ...f, currency: 'USD' })); setShowForm(true); }} className="border px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">Tout payer en USD</button>
              <button onClick={() => { setForm(f => ({ ...f, currency: 'CDF' })); setShowForm(true); }} className="border px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">Tout payer en CDF</button>
              <button onClick={() => setShowForm(true)} className="border px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">Paiement mixte / partiel</button>
            </div>
          </>
        ) : (
          <p className="text-gray-400 text-sm">Aucun paiement en attente</p>
        )}

        {/* Payment form dialog */}
        {showForm && (
          <div className="border rounded-xl p-5 bg-gray-50">
            <h4 className="font-semibold mb-3">Enregistrer un paiement</h4>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Montant</label>
                  <input type="number" step="0.01" min="0" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Devise</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="USD">USD</option>
                    <option value="CDF">CDF</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Reference</label>
                  <input type="text" required value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="REF-..." />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Methode</label>
                  <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="bank_transfer">Virement bancaire</option>
                    <option value="mobile_money">Mobile money</option>
                    <option value="cash">Especes</option>
                    <option value="check">Cheque</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={submitting} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Envoi...' : 'Confirmer le paiement'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">Annuler</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
